/**
 * Proration of a monthly recurring spending across a report range (ATL-1112).
 *
 * A recurring spending is a MONTHLY amount, and the P&L shows what a chosen range of days cost.
 * The rate is therefore the monthly amount divided by the days in THAT month, so a full month
 * costs exactly the monthly amount whatever its length — 105,000 ₸ in 31-day August as in
 * 30-day September. (A fixed /30 divisor would bill a 31-day month more than the bill itself.)
 *
 * Everything here is expressed in BUSINESS DAYS — `yyyy-MM-dd` in the store's own frame, the
 * same day a sale is booked to. That is the point of the module. The report range reaching the
 * P&L screen is a pair of *instants* marking store-local day boundaries, and the previous
 * inline version read their calendar fields with device-local getters (`getMonth()`,
 * `getFullYear()`, `new Date(year, month, day)`). On any device outside the store's timezone a
 * single store day straddles two calendar months in the device's frame, so the month-splitting
 * branch fired and charged that day twice: one September day cost 6,887.10 instead of 3,500,
 * and a full month cost 108,387.10 instead of 105,000. The same report showed different money
 * on two phones. Days carry no clock, so converting to them once, at the edge and in the store's
 * own frame, removes the whole class for this line of the report. Two converters feed it:
 * `businessDateRangeStrings` for the report WINDOW (it inverts the range encoding), and
 * `getStoreLocalDayString` for a spending's own start/end CALENDAR dates — see
 * `recurringSpendingAmount.js` for why those two rules differ.
 */

const DAY_MS = 24 * 60 * 60 * 1000

const pad = (n) => String(n).padStart(2, '0')

/** Parse `yyyy-MM-dd` into its calendar parts. Returns null for anything else. */
const parseDay = (day) => {
  if (typeof day !== 'string') return null
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day)
  if (!match) return null
  return { year: Number(match[1]), month: Number(match[2]), day: Number(match[3]) }
}

/** Days in a 1-indexed month. Day 0 of the next month is the last day of this one. */
const daysInMonth = (year, month) => new Date(Date.UTC(year, month, 0)).getUTCDate()

/** Inclusive count of days from `fromDay` to `toDay`, both `yyyy-MM-dd`. */
const inclusiveDayCount = (fromDay, toDay) => {
  const from = parseDay(fromDay)
  const to = parseDay(toDay)
  if (!from || !to) return 0
  const fromMs = Date.UTC(from.year, from.month - 1, from.day)
  const toMs = Date.UTC(to.year, to.month - 1, to.day)
  if (toMs < fromMs) return 0
  return Math.round((toMs - fromMs) / DAY_MS) + 1
}

/**
 * What a monthly recurring spending costs over a range of business days.
 *
 * @param {object} params
 * @param {number} params.amount          The monthly amount.
 * @param {string} params.startDay        First business day of the report range, `yyyy-MM-dd`.
 * @param {string} params.endDay          Last business day of the report range, inclusive.
 * @param {string} params.activeFromDay   First business day the spending is active. Required —
 *                                        a spending with no start day is not active at all.
 * @param {?string} params.activeUntilDay Last active business day, inclusive; null = open ended.
 * @returns {number} The prorated amount, and 0 (never NaN) for any range it does not cover.
 */
export const prorateMonthlyRecurringSpending = ({
  amount,
  startDay,
  endDay,
  activeFromDay,
  activeUntilDay = null,
}) => {
  // Coerce before the finite check. PostgREST serialises `numeric` unquoted so `amount` is a
  // number today, but if it ever arrived as a numeric STRING the guard would return 0 — and a
  // silent 0 here is indistinguishable from "the spending is not active", i.e. an expense that
  // quietly vanishes from the P&L rather than failing.
  const monthlyAmount = Number(amount)
  if (!Number.isFinite(monthlyAmount) || monthlyAmount === 0) return 0
  if (!parseDay(startDay) || !parseDay(endDay) || !parseDay(activeFromDay)) return 0

  // Clamp the range to the spending's active window. ISO days sort lexicographically, so
  // string comparison is calendar comparison.
  const from = startDay > activeFromDay ? startDay : activeFromDay
  const to = activeUntilDay && endDay > activeUntilDay ? activeUntilDay : endDay
  if (from > to) return 0

  const last = parseDay(to)
  let { year, month } = parseDay(from)
  let total = 0

  while (year < last.year || (year === last.year && month <= last.month)) {
    const length = daysInMonth(year, month)
    const monthStart = `${year}-${pad(month)}-01`
    const monthEnd = `${year}-${pad(month)}-${pad(length)}`

    const segmentStart = from > monthStart ? from : monthStart
    const segmentEnd = to < monthEnd ? to : monthEnd
    const days = inclusiveDayCount(segmentStart, segmentEnd)

    if (days > 0) total += (monthlyAmount / length) * days

    month += 1
    if (month > 12) {
      month = 1
      year += 1
    }
  }

  return total
}
